import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { UtilitiesService } from '..';

import { AddJeopardyGameInput, AddJeopardyUserAnswerInput, JeopardyGame, JeopardyGameFilterInput, JeopardyGameSortInput, JeopardyQuestion, JeopardyRoom, JeopardyRoomSortInput, JeopardyRound, JeopardyUser, JeopardyUserFilterInput, JeopardyUsersConnection, JeopardyUserSortInput, PayloadEntityOfJeopardyRound, PayloadEntityOfJeopardyUser, PayloadEntityOfJeopardyUserAnswer, UpdateJeopardyUserInput } from '@app/shared/graphql/types';
import { QuestionsService } from '@app/shared/rest-api';
import { DataService } from '../data.service';
import { joinRoomMutation } from './mutations';
import { getRoomNamesQuery, getRoomsListQuery } from './queries';
import {
    beforeRoundStartedSubscription,
    expiringRoundSubscription,
    gameInactivatedSubscription,
    roundEndedSubscription,
    roundStartedSubscription,
    userConnectedSubscription,
    userIdleDisconnectedSubscription,
    userIdleSubscription,
    userScoreUpdatedSubscription
} from './subscriptions';

export interface DifficultyLevel {
  points: number;
  imagePath: string;
  disabledPath: string;
}

export interface CustomTopic {
  id: string;
  name: string;
  start: number;
  end: number;
  icon: string;
iconDescription: string;
defaultPictureUrl: string;
defaultPictureUrlDescription: string;
}

export interface VersionDetails {
version: {
  buildDate: string;
  company: string;
  name: string;
  version: string;
};
}

const getGameRoundsQuery = gql`
  query getGameRounds($gameId: Int!) {
    rounds:jeopardyRoundsByGameId(gameId:$gameId) {
      gameId,
      roundId,
      roundNumber,
      timeStart,
      timeEnd,
      activeUser {
        deviceIdentifier
        deviceDetected
        avatar
        score
        connected
        language
        location
        playInactivityCounter
        playingTime
        userId
        name
        jokerSkip
        jokerFiftyFifty
        host
      },
      jeopardyUserAnswers {
        userId
        userAnswerId
        trueAnswerIndex
        selectedAnswerIndex
        points
        skip
        roundId
        user {
          deviceIdentifier
          deviceDetected
          avatar
          score
          connected
          language
          location
          playInactivityCounter
          playingTime
          userId
          name
          jokerSkip
          jokerFiftyFifty
          host
        }
     }
    }
  }
`;

const getCurrentRoundQuery = gql`
  query getCurrentRound($gameId: Int!) {
    round:jeopardyCurrentRoundByGameId(gameId:$gameId) {
      gameId,
      roundId,
      roundNumber,
      timeStart,
      timeEnd,
      remainingAnswerTime
      activeUser {
        deviceIdentifier
        deviceDetected
        avatar
        score
        connected
        language
        location
        playInactivityCounter
        playingTime
        userId
        name
        jokerSkip
        jokerFiftyFifty
        host
      },
      jeopardyUserAnswers {
        userId
        userAnswerId
        trueAnswerIndex
        selectedAnswerIndex
        points
        skip
        roundId
        user {
          deviceIdentifier
          deviceDetected
          avatar
          score
          connected
          language
          location
          playInactivityCounter
          playingTime
          userId
          name
          jokerSkip
          jokerFiftyFifty
          host
        }
      }
      question {
        questionId
        umbracoQuestionId
        points
        topicId
        timer
      }
    }
  }
`;

const getRoundQuestionQuery = gql`
query getRoundQuestion($gameId: Int!, $topicId: UUID!, $points: Int!) {
     question:jeopardyQuestions (
       gameId: $gameId,
       topicId: $topicId,
       points: $points,
      ) {
        questionId,
        umbracoQuestionId
    }
}`;

const setQuestionPointsMutation = gql`
  mutation chooseQuestionPoints($gameId: Int!, $userId: Int!, $questionId: Int!) {
     chooseQuestionPoints (
       gameId: $gameId,
       userId: $userId
       questionId: $questionId
      ) {
        entity {
          roundId,
          roundNumber
        }
        errors {
          message
        }
  }
}`;

const getUsersQuery = gql`
  query jeopardyUsers($first: Int, $after: String, $last: Int, $before: String, $where: JeopardyUserFilterInput, $order: [JeopardyUserSortInput!]) {
      users:jeopardyUsers (
        first: $first
        after: $after
        last: $last
        before: $before
        where: $where
        order: $order
      ) {
        nodes {
          userId
          name
          score
          location
          avatar
        }
        pageInfo {
          startCursor
          endCursor
          hasPreviousPage
          hasNextPage
        }
    }
  }`;

const getUserByGameIdQuery = gql`
  query jeopardyUsersByGameId($gameId: Int!) {
    users:jeopardyUsersByGameId(
      gameId: $gameId,
    ) {
      userId,
      deviceDetected,
      connected
      name
      deviceIdentifier
      playInactivityCounter
      jokerSkip
      jokerFiftyFifty
      score
      language
      location
      playingTime
      avatar
      host
    }
  }
`;

const getUserByIdQuery = gql`
  query jeopardyUserById($id: Int!) {
    user:jeopardyUserById(
      id: $id
    ) {
      userId,
      deviceDetected,
      connected
      name
      host
      deviceIdentifier
      playInactivityCounter
      jokerSkip
      jokerFiftyFifty
      score
      language
      location
      playingTime
      avatar
    }
  }
`;

const disabledQuestionsQuery = gql`
  query getDisabledQuestions($gameId: Int!) {
    questions:jeopardyQuestionsByGameId(
      gameId: $gameId,
      where: {
        active: { eq: false }
      }
    ) {
      questionId
      topicId
      points
  }
  }
`;

const getGameDetailsQuery = gql`
  query getGameDetails($id: Int!) {
    game:jeopardyGameById(
      id: $id,
    ) {
      gameId
      active
      multiplayer
      createdOn
      roomName
      players
      private
      active
      startDateTime
      endDateTime
      gameEngine {
        gameEngineId
        activation
        settingTimerJoinRoom
        settingJokerSkip
        settingJoker5050
        settingDefaultScore
      }
      jeopardyUsers {
        userId
        name
        avatar
        host
        deviceDetected
        deviceIdentifier
        connected
        location
        language
        score
        jokerSkip
        jokerFiftyFifty
        playInactivityCounter
        playingTime
      }
    }
  }
`;

const provideAnswerToQuestionMutation = gql`
  mutation provideAnswerToQuestion($gameId: Int!, $input: AddJeopardyUserAnswerInput) {
    provideAnswerToQuestion (
       gameId: $gameId,
       input: $input
      ) {
        entity {
          roundId
          trueAnswerIndex
          selectedAnswerIndex
          points
        }
        errors {
          message
        }
  }
}`;

const updateUserMutation = gql`
  mutation updateJeopardyUser($input: UpdateJeopardyUserInput) {
    user: updateJeopardyUser(input: $input) {
      entity {
        userId,
        name,
        deviceDetected,
        deviceIdentifier,
        gameId
      },
      errors {
        message
        code
      }
    }
  }
`;

const getVersionQuery = gql`
  query {
    version {
      buildDate
      name
      company
      version
    }
  }
`;

const GAME_ID_KEY = 'j-game';
const BROWSER_ID_KEY = 'j-brw';

@Injectable({
  providedIn: 'root',
})
export class GameService {
  public game: BehaviorSubject<JeopardyGame>;

  public userDisconnected: BehaviorSubject<boolean>;
  public userIdle: BehaviorSubject<boolean>;

  private subscriptions: Subscription;

  constructor(
    private apollo: Apollo,
    private questionService: QuestionsService,
    private ds: DataService,
  ) {
    this.subscriptions = null;
    this.game = new BehaviorSubject(null);

    this.userIdle = new BehaviorSubject(false);
    this.userDisconnected = new BehaviorSubject(false);
  }

  public setDeviceDetails(utilitiesService: UtilitiesService) {
    const _navigator = navigator;
    const prevBrowserId = localStorage.getItem(BROWSER_ID_KEY);

    if (!prevBrowserId) {
        let hashedUserAgent = utilitiesService.getHash(JSON.stringify(_navigator));
        if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ) {
            hashedUserAgent  = 'mobile-' + hashedUserAgent;
        } else {
            hashedUserAgent  = 'desktop-' + hashedUserAgent;
        }
        localStorage.setItem(BROWSER_ID_KEY, hashedUserAgent);
    }
  }

  public getDeviceDetails() {
    return localStorage.getItem(BROWSER_ID_KEY).split('-');
  }

  public createGame(params: AddJeopardyGameInput) {
    return this.ds.createJeopardyGame(params).pipe(map(result => result?.entity));
  }

  public setGameId(gameId: number | '') {
    if(!gameId) {
      localStorage.setItem(GAME_ID_KEY, null);
    } else {
      localStorage.setItem(GAME_ID_KEY, gameId.toString());
    }
  }

  public getGameId() {
    return localStorage.getItem(GAME_ID_KEY);
  }

  public finishGame() {
    this.game.next(null);
    return localStorage.setItem(GAME_ID_KEY, null);
  }

  public getGameRounds(gameId: number): Observable<JeopardyRound[]> {
    const variables = {
      gameId
    };

    return this.apollo.query<{rounds: JeopardyRound[]}>({ 
      query: getGameRoundsQuery,
      variables
    })
    .pipe(
      map(response => response.data.rounds)
    );
  }

  public getGameRound(gameId: number): Observable<JeopardyRound> {
    const variables = {
      gameId
    };

    return this.apollo.query<{round: JeopardyRound}>({ 
      query: getCurrentRoundQuery,
      variables
    })
    .pipe(
      map(response => response.data.round)
    );
  }

  public getUsers(order: JeopardyUserSortInput[], first?: number, after?: string, last?: number, before?: string, where?: JeopardyUserFilterInput): Observable<JeopardyUsersConnection> {
    const variables = {
      order, 
      first, 
      after, 
      last, 
      before, 
      where
    };
    
    return this.apollo.query<{users: JeopardyUsersConnection}>({ 
      query: getUsersQuery,
      variables
    }).pipe(
      map(response => response.data.users)
    );
  }

  public getUserByGameId(gameId: number): Observable<JeopardyUser[]> {
    const variables = {
      gameId
    };

    return this.apollo.query<{users: JeopardyUser[]}>({ 
      query: getUserByGameIdQuery,
      variables
    })
    .pipe(
      map(response => response.data.users)
    );
  }

  public getUserById(id: number): Observable<JeopardyUser> {
    const variables = {
      id
    };

    return this.apollo.query<{user: JeopardyUser}>({ 
      query: getUserByIdQuery,
      variables
    })
    .pipe(
      map(response => response.data.user)
    );
  }

  public getRoundQuestionQuery(gameId: number, topicId: string, points: number): Observable<JeopardyQuestion[]> {
    const variables = {
      gameId,
      topicId,
      points,
    };

    return this.apollo.query<{question: JeopardyQuestion[]}>({ 
      query: getRoundQuestionQuery,
      variables: variables
    })
    .pipe(
      map(response => response.data.question)
    );
  }

  public updateUser(input: UpdateJeopardyUserInput) {
    const variables = {
      input
    };

    return this.apollo.mutate<{ user: { }}>({ 
      mutation: updateUserMutation,
      variables: variables
    })
    .pipe(
      map(response => response.data.user)
    );
  }

  public setRoundQuestion(gameId: number, userId: number, questionId: number) {
    const variables = {
      gameId,
      userId,
      questionId
    };

    return this.apollo.mutate<PayloadEntityOfJeopardyRound>({ 
      mutation: setQuestionPointsMutation,
      variables: variables
    })
    .pipe(
      map(response => response.data.entity)
    );
  }

  public getQuestionTranslation(questionId: string, lang: string) {
    return this.questionService.apiQuestionsRepositoryQuestionsQuestionIdGet(questionId, lang);
  }

  public getDisabledQuestions(gameId: number): Observable<JeopardyQuestion[]> {
    const variables = {
      gameId
    };

    return this.apollo.query<{questions: JeopardyQuestion[]}>({ 
      query: disabledQuestionsQuery,
      variables: variables
    })
    .pipe(
      map(response => response.data.questions)
    );
  }

  public getGameDetails(gameId: number): Observable<JeopardyGame> {
    if(!this.game.getValue()) {
      const variables = {
        id: gameId
      };
  
      return this.apollo.query<{game: JeopardyGame}>({ 
        query: getGameDetailsQuery,
        variables: variables
      })
      .pipe(
        map(response => response.data.game),
        tap(game => this.game.next(game))
      );
    } else {
      // console.log(this.game.getValue());
      return this.game.asObservable();
    }
    
  }

  public provideAnswerToQuestion(gameId: number, input: AddJeopardyUserAnswerInput) {
    const variables = {
      gameId,
      input,
    };

    return this.apollo.mutate<{provideAnswerToQuestion: PayloadEntityOfJeopardyUserAnswer}>({ 
      mutation: provideAnswerToQuestionMutation,
      variables: variables
    })
    .pipe(
      map(response => response.data.provideAnswerToQuestion.entity)
    );
  }

  public getGameInactivatedSubscription(gameId: number): Observable<JeopardyRound> {
    const variables = {
      gameId,
    };

    return this.apollo.subscribe<{round: JeopardyRound}>({
      query: gameInactivatedSubscription,
      variables: variables
    }).pipe(
      map(response => response.data.round)
    );
  }

  public getBeforeRoundStartedSubscription(gameId: number): Observable<JeopardyRound> {
    const variables = {
      gameId,
    };

    return this.apollo.subscribe<{round: JeopardyRound}>({
      query: beforeRoundStartedSubscription,
      variables: variables
    }).pipe(
      map(response => response.data.round)
    );
  }

  public getRoundStartedSubscription(gameId: number): Observable<JeopardyRound> {
    const variables = {
      gameId,
    };

    return this.apollo.subscribe<{round: JeopardyRound}>({
      query: roundStartedSubscription,
      variables: variables
    }).pipe(
      map(response => response.data.round)
    );
  }

  public getRoundEndedSubscription(gameId: number): Observable<JeopardyRound> {
    const variables = {
      gameId,
    };

    return this.apollo.subscribe<{round: JeopardyRound}>({
      query: roundEndedSubscription,
      variables: variables
    }).pipe(
      map(response => response.data.round)
    );
  }

  public getUserIdleSubscription(gameId: number): Observable<JeopardyUser> {
    const variables = {
      gameId,
    };

    return this.apollo.subscribe<{user: JeopardyUser}>({
      query: userIdleSubscription,
      variables: variables
    }).pipe(
      map(response => response.data.user)
    );
  }

  public getUserIdleDisconnectedSubscription(gameId: number): Observable<JeopardyUser> {
    const variables = {
      gameId,
    };

    return this.apollo.subscribe<{user: JeopardyUser}>({
      query: userIdleDisconnectedSubscription,
      variables: variables
    }).pipe(
      map(response => response.data.user)
    );
  }

  public getUserConnectedSubscription(gameId: number): Observable<JeopardyUser> {
    const variables = {
      gameId,
    };

    return this.apollo.subscribe<{user: JeopardyUser}>({
      query: userConnectedSubscription,
      variables: variables
    }).pipe(
      map(response => response.data.user)
    );
  }

  public getUserScoreUpdatedSubscription(gameId: number): Observable<JeopardyUser> {
    const variables = {
      gameId,
    };

    return this.apollo.subscribe<{user: JeopardyUser}>({
      query: userScoreUpdatedSubscription,
      variables: variables
    }).pipe(
      map(response => response.data.user)
    );
  }

  public getExpiringRoundSubscription(gameId: number): Observable<JeopardyRound> {
    const variables = {
      gameId,
    };

    return this.apollo.subscribe<{ beforeRoundStart: JeopardyRound }>({
      query: expiringRoundSubscription,
      variables: variables
    }).pipe(
      map(response => response.data.beforeRoundStart)
    );
  }

  public startSubscriptions() {
    this.subscriptions = new Subscription();
    const gameId = +this.getGameId();

    const beforeRoundStartedSub = this.getBeforeRoundStartedSubscription(gameId)
      .subscribe(() => {console.log('beforeRoundStartedSub')});
    const roundStartedSub = this.getRoundStartedSubscription(gameId)
      .subscribe(() => {console.log('roundStartedSub')});
    const roundEndedSub = this.getRoundEndedSubscription(gameId)
      .subscribe(() => {console.log('roundEndedSub')});
    const userIdleSub = this.getUserIdleSubscription(gameId)
      .subscribe(() => {console.log('userIdleSub')});
    const userDisconnectedSub = this.getUserIdleDisconnectedSubscription(gameId)
      .subscribe(() => {console.log('userDisconnectedSub')});
    const userScoreUpdatedSub = this.getUserScoreUpdatedSubscription(gameId)
      .subscribe(() => {console.log('userScoreUpdatedSub')});
    const gameInactivatedSub = this.getGameInactivatedSubscription(gameId)
      .subscribe(() => {console.log('gameInactivatedSub')});

    this.subscriptions.add(beforeRoundStartedSub);
    this.subscriptions.add(roundStartedSub);
    this.subscriptions.add(roundEndedSub);
    this.subscriptions.add(userIdleSub);
    this.subscriptions.add(userDisconnectedSub);
    this.subscriptions.add(userScoreUpdatedSub);
    this.subscriptions.add(gameInactivatedSub);
  }

  public clearSubscriptions() {
    if(this.subscriptions) {
      console.log('no subs');
      this.subscriptions.unsubscribe();
    }
  }

  public getRoomsList(query: string): Observable<JeopardyGame[]> {
    const where: JeopardyGameFilterInput = {
      roomName: { contains: query }
    };
    const order: JeopardyGameSortInput[] = null;

    return this.apollo.query<{rooms: JeopardyGame[]}>({ 
      query: getRoomsListQuery,
      variables: { order, where },
    })
    .pipe(
      map(response => response.data.rooms)
    );
  }

  public getRoomNames(): Observable<JeopardyRoom[]> {
    const order: JeopardyRoomSortInput[] = null;

    return this.apollo.query<{rooms: JeopardyRoom[]}>({ 
      query: getRoomNamesQuery,
      variables: { order },
    })
    .pipe(
      map(response => response.data.rooms)
    );
  }

  public joinRoom(accessCode: string | null, lang: string, gameId: number) {
    const variables = {
      accessCode,
      lang,
      gameId,
    };

    return this.apollo.mutate<PayloadEntityOfJeopardyUser>({ 
      mutation: joinRoomMutation,
      variables: variables
    })
    .pipe(map((response) => response.data));
  }

  public getVersion(): Observable<VersionDetails> {
    return this.apollo
      .query<VersionDetails>({
        query: getVersionQuery,
        variables: {},
      })
      .pipe(map((response) => response.data));
  }

  public getDifficultyLevels(): Observable<DifficultyLevel[]> {
     const difficultyLevels = [
      {
        points: 100,
        imagePath: 'assets/app-branding/img/jetons/red.png',
        disabledPath: 'assets/app-branding/img/jetons/red_flipped.png'
      },
      {
        points: 200,
        imagePath: 'assets/app-branding/img/jetons/green.png',
        disabledPath: 'assets/app-branding/img/jetons/green_flipped.png'
      },
      {
        points: 300,
        imagePath: 'assets/app-branding/img/jetons/black.png',
        disabledPath: 'assets/app-branding/img/jetons/black_flipped.png'
      },
      {
        points: 400,
        imagePath: 'assets/app-branding/img/jetons/blue.png',
        disabledPath: 'assets/app-branding/img/jetons/blue_flipped.png'
      },
      {
        points: 500,
        imagePath: 'assets/app-branding/img/jetons/yellow.png',
        disabledPath: 'assets/app-branding/img/jetons/yellow_flipped.png'
      },
    ]; 

    return of(difficultyLevels);
  }
}
